/** Deploy-time runtime configuration, served as /config.json at the site root.
 *
 *  Local dev and the committed Python-package bundle have no config.json: the
 *  boot sequence falls back to live mode (same-origin /api against the local
 *  FastAPI server, no auth). The hosted site ships `mode: "static"` plus the
 *  Guardius coordinates, which switches the SPA to signed-in, read-only mode.
 *
 *  Ported from penseur-trading's lib/auth.tsx (loadRuntimeConfig + ConfigError),
 *  including the vite-dev nuance documented on ConfigErrorKind. */

export interface RuntimeConfig {
  apiUrl: string;
  guardiusUrl: string;
  tenantId: string;
  mode: "static" | "live";
}

/** The local-dev / packaged-bundle fallback: same-origin live API, no auth. */
export const LOCAL_CONFIG: RuntimeConfig = {
  apiUrl: "",
  guardiusUrl: "",
  tenantId: "",
  mode: "live",
};

/** Failure kinds for runtime-config loading:
 *
 *    - 'not-found' : HTTP 404 — config genuinely absent (a real deploy always
 *                    ships config.json, so 404 means we're in local dev).
 *    - 'http'      : any other non-2xx — a deployed config the origin refused
 *                    or failed to serve. Must surface loudly.
 *    - 'parse'     : 2xx but the body isn't valid JSON. `contentType` carries
 *                    the response's Content-Type: in local dev the vite server
 *                    has no /config.json and its SPA history fallback returns
 *                    index.html with 200 + text/html, so an absent config in
 *                    dev looks like a parse failure. Only that text/html
 *                    signature is treated as the dev fallback; a corrupt
 *                    deployed config.json (application/json) stays loud.
 *    - 'network'   : the fetch itself rejected. Ambiguous; surfaced loudly. */
export type ConfigErrorKind = "not-found" | "http" | "parse" | "network";

export class ConfigError extends Error {
  constructor(
    public readonly kind: ConfigErrorKind,
    message: string,
    public readonly status?: number,
    public readonly contentType?: string | null,
  ) {
    super(message);
    this.name = "ConfigError";
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let res: Response;
  try {
    res = await fetch("/config.json");
  } catch (e) {
    throw new ConfigError(
      "network",
      `config.json fetch failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (!res.ok) {
    const kind: ConfigErrorKind = res.status === 404 ? "not-found" : "http";
    throw new ConfigError(kind, `config.json load failed: ${res.status}`, res.status);
  }

  try {
    return (await res.json()) as RuntimeConfig;
  } catch (e) {
    throw new ConfigError(
      "parse",
      `config.json is not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
      res.status,
      res.headers.get("content-type"),
    );
  }
}

/** Load the deployed config, falling back to {@link LOCAL_CONFIG} only for the
 *  two absent-config signatures (404, or the vite SPA-fallback text/html parse
 *  failure). Every other failure mode re-throws for a loud error screen. */
export async function resolveBootConfig(): Promise<RuntimeConfig> {
  try {
    return await loadRuntimeConfig();
  } catch (e) {
    if (
      e instanceof ConfigError &&
      (e.kind === "not-found" ||
        (e.kind === "parse" && (e.contentType ?? "").includes("text/html")))
    ) {
      return LOCAL_CONFIG;
    }
    throw e;
  }
}
